import { useEffect, useState } from "react";
import CameraCapture from "../components/CameraCapture";
import { matchStreet, streetCanon, looksLikeStreet } from "../utils/streetMatch";
import { Search, X, CornerDownLeft, Loader2, MapPin, AlertTriangle, CheckCircle2, RotateCcw } from "lucide-react";

/* ------------------------------------------------------------------ *
 * Depot address scan / search.
 *
 * Point a phone at an AP address label (or type the street) → it reads the
 * street, matches it across every driver's route, and shows which DRIVER and
 * which PHYSICAL FLOOR BAY it belongs to on a full-screen GREEN result.
 * Shared streets (split by house-number range) ask for the number (amber);
 * an address on no route, or a label it can't read, FLASHES RED.
 *
 * `routes` come from the floor plan already carrying the physical floor bay
 * and the driver's programmed identity:
 *   { login, streets[], physicalBay, programmedBay, programmedFull, wave, area }
 *
 * Street matching (norm / ranges / streetNameOf / resolveAddress) mirrors the
 * proven sorters-portal label scan (ChuteLayoutTab) so behaviour is identical.
 * ------------------------------------------------------------------ */

const OCR_API = "/.netlify/functions/ocr-sort-label";

// A house-number range: "400-420", "424 - 428", and — crucially — "180.-234" (AP data
// carries a stray dot after the first number). The dot/comma before the dash is tolerated.
const RANGE_RX = () => /(\d+)\s*[.,]?\s*[-–]\s*(\d+)/g;
function extractRanges(entry) {
  const r = [], rx = RANGE_RX();
  let m;
  while ((m = rx.exec(entry)) !== null) r.push([+m[1], +m[2]]);
  return r;
}
// The numeric coverage a route entry claims: ranges (e.g. "400 - 420") AND bare specific
// numbers (e.g. "95 PITT ST", "93, 95 LIVERPOOL ST") as exact [n,n] points. A single
// number is NOT a catch-all — it matches only itself. Only a digit-less entry claims nothing.
function entryBands(entry) {
  const bands = extractRanges(entry);
  const rest = String(entry).replace(RANGE_RX(), " "); // drop ranges, keep the rest
  const rx = /\d+/g;
  let m;
  while ((m = rx.exec(rest)) !== null) bands.push([+m[0], +m[0]]);
  return bands;
}
function inRange(num, lo, hi) {
  const mn = Math.min(lo, hi), mx = Math.max(lo, hi);
  if (num < mn || num > mx) return false;
  const loOdd = lo % 2 !== 0, hiOdd = hi % 2 !== 0;
  if (loOdd === hiOdd) return (num % 2 !== 0) === loOdd; // matched-parity band
  return true; // mixed parity → whole run
}
// Human-readable street name for DISPLAY (keeps case): drop the number/range only.
const streetNameOf = (entry) =>
  String(entry).replace(RANGE_RX(), "").replace(/[.,]/g, " ").replace(/\s+/g, " ").trim();
// Which route entries name the street we're resolving? Street identity lives in
// utils/streetMatch — the name words AND the type must agree. The old key was
// plain text containment, so "Bridge Road" resolved onto "Pyrmont bridge rd" — a
// different street, a different area, a different driver, and a parcel on the
// wrong bay. Prose on a route list ("Loading Bay Dawson Street") still matches
// the street it wraps, and "Road" now matches a list that writes "Rd".
const sameStreetAs = (entry, street) => matchStreet(entry, street) !== null;

/** Resolve a scanned/typed street (+ optional house number) across all routes. */
export function resolveAddress(routes, rawStreet, num) {
  if (streetCanon(rawStreet).replace(/[^A-Z]/g, "").length < 3) return { status: "no", name: rawStreet, num };

  // rangeByLogin: the number lands inside this driver's range/point — the precise owner.
  // wholeByLogin: the driver lists the street with NO number at all (a true catch-all like "George Street").
  // needNum:      the driver has a numbered entry but we don't have a house number yet.
  const rangeByLogin = new Map(), wholeByLogin = new Map(), needNum = new Map();
  (routes || []).forEach((rt) =>
    (rt.streets || []).forEach((entry) => {
      if (!sameStreetAs(entry, rawStreet)) return;
      const ranges = entryBands(entry);
      const rec = { rt, entry, ranges };
      if (!ranges.length) { if (!wholeByLogin.has(rt.login)) wholeByLogin.set(rt.login, rec); return; }
      if (num == null) { if (!needNum.has(rt.login)) needNum.set(rt.login, rec); return; }
      if (ranges.some(([lo, hi]) => inRange(num, lo, hi))) rangeByLogin.set(rt.login, rec);
    })
  );

  const label = streetNameOf(rawStreet) || rawStreet;
  const rangeHits = [...rangeByLogin.values()];
  const wholeHits = [...wholeByLogin.values()];

  // A house number that falls inside a driver's explicit range is the direct hit and
  // must rank first — above any driver who only lists the street with no number.
  // Those trail as "also on" (e.g. 428 George St → PETRATOSS, not the bare-"George Street" round).
  if (num != null && rangeHits.length) {
    const others = [...rangeHits.slice(1), ...wholeHits];
    return others.length
      ? { status: "ok", name: label, num, rec: rangeHits[0], multi: [rangeHits[0], ...others] }
      : { status: "ok", name: label, num, rec: rangeHits[0] };
  }

  // No explicit range owns the number → fall back to whole-street (catch-all) claimants.
  if (wholeHits.length === 1) return { status: "ok", name: label, num, rec: wholeHits[0] };
  if (wholeHits.length > 1) return { status: "ok", name: label, num, rec: wholeHits[0], multi: wholeHits };
  if (needNum.size) return { status: "ask", name: label, num, options: [...needNum.values()] };

  const ranged = new Map();
  (routes || []).forEach((rt) =>
    (rt.streets || []).forEach((entry) => {
      const bands = entryBands(entry);
      if (sameStreetAs(entry, rawStreet) && bands.length && !ranged.has(rt.login))
        ranged.set(rt.login, { rt, entry, ranges: bands });
    })
  );
  if (ranged.size && num != null) return { status: "outofrange", name: label, num, options: [...ranged.values()] };

  // Nothing holds this street. A near-identical spelling on a route list ("Bride
  // rd" for Bridge Rd, live on a Broadway round) is offered as a QUESTION, never
  // as a match — only a person can tell a typo from a genuinely other street.
  const near = [];
  (routes || []).forEach((rt) =>
    (rt.streets || []).forEach((entry) => {
      if (looksLikeStreet(entry, rawStreet) && !near.some((n) => n.rt.login === rt.login)) near.push({ rt, entry });
    })
  );
  return { status: "no", name: label, num, near: near.slice(0, 3) };
}

/* ------------------------------------------------------------------ */

export default function DepotAddressScan({ routes }) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);

  return (
    <>
      <style>{FLASH_CSS}</style>
      <button style={S.trigger} onClick={() => setOpen(true)}>
        <Search size={15} /> Find / scan address
      </button>

      {open && (
        <ScanModal
          routes={routes}
          onClose={() => setOpen(false)}
          onResult={(r) => { setOpen(false); setResult(r); }}
        />
      )}

      {result && (
        <ResultOverlay
          result={result}
          onScanAgain={() => { setResult(null); setOpen(true); }}
          onClose={() => setResult(null)}
          onEnterNumber={(n) => setResult({ ...resolveAddress(routes, result.name, n), scanned: result.scanned })}
        />
      )}
    </>
  );
}

/** Driver-portal wrapper: loads the live routes + physical-bay overlay itself
 *  (via the session-gated endpoints) and renders the scanner. Lets any signed-in
 *  driver scan a label and see which driver/bay an address belongs to. */
export function PortalAddressScan() {
  const [routes, setRoutes] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let live = true;
    const rk = (r) => {
      const a = String(r.adamCode || "").trim();
      return a || (r.driverLogin ? `L:${String(r.driverLogin).toUpperCase()}` : "");
    };
    Promise.all([
      fetch("/.netlify/functions/driver-routes-sb").then((r) => r.json()),
      fetch("/.netlify/functions/depot-layout-sb", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "getLayout" }),
      }).then((r) => (r.ok ? r.json() : { overrides: {} })).catch(() => ({ overrides: {} })),
    ])
      .then(([rd, ld]) => {
        if (!live) return;
        if (!rd || rd.success === false) { setErr((rd && rd.error) || "Could not load routes."); return; }
        const overrides = (ld && ld.overrides) || {};
        const sr = (rd.routes || []).map((r) => {
          const parts = String(r.adamCode || "").split("_");
          const progBay = parts[0] === "BAY" && parts[1] ? parts[1] : String(r.bayNumber || "");
          const dPart = parts.length >= 4 ? parts[parts.length - 1] : (r.dNumber ? `D${r.dNumber}` : "");
          const ov = overrides[rk(r)];
          const physicalBay = ov && "bay" in ov ? String(ov.bay) : String(r.bayNumber || "");
          return {
            login: r.driverLogin || "",
            streets: Array.isArray(r.streets) ? r.streets : [],
            physicalBay,
            programmedBay: progBay,
            programmedFull: String(r.adamCode || "") || [progBay, dPart].filter(Boolean).join(" ") || "—",
            wave: ov && "wave" in ov ? String(ov.wave) : String(r.wave || ""),
            area: r.area || "",
          };
        });
        setRoutes(sr);
      })
      .catch(() => { if (live) setErr("Could not load routes."); });
    return () => { live = false; };
  }, []);

  return (
    <div style={{ padding: "18px 16px", maxWidth: 560, margin: "0 auto" }}>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem", margin: "0 0 6px", color: "var(--hp-text)" }}>
        Find a delivery address
      </h2>
      <p style={{ fontSize: "0.86rem", color: "var(--hp-dim)", margin: "0 0 18px", lineHeight: 1.5 }}>
        Scan a parcel label or type the street — it shows which driver and floor bay it belongs to. Handy for a parcel that isn’t on your round.
      </p>
      {routes === null && !err && <div style={{ color: "var(--hp-dim)", fontSize: "0.85rem" }}>Loading routes…</div>}
      {err && <div style={{ color: "var(--hp-red, #EF4444)", fontSize: "0.85rem", fontWeight: 600 }}>{err}</div>}
      {routes && <DepotAddressScan routes={routes} />}
    </div>
  );
}

function ScanModal({ routes, onClose, onResult }) {
  const [img, setImg] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [manual, setManual] = useState("");

  const runOcr = async (dataUrl) => {
    setImg(dataUrl);
    setErr("");
    setBusy(true);
    try {
      const r = await fetch(OCR_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageBase64: dataUrl.split(",")[1], mediaType: "image/jpeg" }),
      }).then((x) => x.json());
      if (r && r.success && r.street && streetNameOf(r.street)) {
        onResult({ ...resolveAddress(routes, r.street, r.number), scanned: r });
      } else {
        // Couldn't read a street → flash red so it's obvious the label was bad/dirty.
        onResult({ status: "unreadable", name: (r && r.raw) || "" });
      }
    } catch {
      onResult({ status: "unreadable", name: "" });
    } finally {
      setBusy(false);
    }
  };

  const runManual = () => {
    const v = manual.trim();
    if (!v) return;
    const numM = v.match(/\b(\d{1,5})\b/);
    onResult(resolveAddress(routes, v, numM ? parseInt(numM[1], 10) : null));
  };

  return (
    <div onClick={onClose} style={S.modalWrap}>
      <div onClick={(e) => e.stopPropagation()} style={S.modal}>
        <div style={S.modalHead}>
          <span style={S.modalTitle}>Find / scan address</span>
          <button onClick={onClose} style={S.iconBtn}><X size={20} /></button>
        </div>
        <p style={S.modalSub}>
          Point the camera at the delivery address on the label. It reads the street and shows the driver and floor bay it belongs to.
        </p>

        <CameraCapture nativeFirst guide="document" maxEdge={1280} quality={0.8} value={img} onCapture={runOcr} onClear={() => setImg("")} />

        {busy && (
          <div style={S.reading}><Loader2 size={16} style={{ animation: "hp-spin 0.8s linear infinite" }} /> Reading label…</div>
        )}
        {err && <div style={S.modalErr}>{err}</div>}

        <div style={S.orBlock}>
          <div style={S.orLabel}>Or type the address</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runManual(); }}
              placeholder="e.g. 142 Commonwealth Street"
              autoCapitalize="none"
              style={S.input}
            />
            <button onClick={runManual} style={S.findBtn}><CornerDownLeft size={15} /> Find</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultOverlay({ result, onScanAgain, onClose, onEnterNumber }) {
  const [askVal, setAskVal] = useState(result.num != null ? String(result.num) : "");
  const rangeText = (ranges) => ranges.map(([lo, hi]) => (lo === hi ? `${lo}` : `${lo}–${hi}`)).join(", ");
  // When the label carried more than one number (a suite/level in front of the
  // real street number, e.g. "Se 12 L 7 428 George St"), let the user pick which
  // one is the street number — it re-resolves the driver/bay instantly.
  const cands = Array.isArray(result.scanned?.numbers) ? result.scanned.numbers : [];
  const showChooser = onEnterNumber && cands.length >= 2;
  let bg, flashing = false, body;

  if (result.status === "ok") {
    const rt = result.rec.rt;
    bg = "linear-gradient(160deg, #22a35f, #157f47)";
    const ranges = result.rec.ranges || [];
    const moved = rt.physicalBay && rt.programmedBay && rt.physicalBay !== rt.programmedBay;
    body = (
      <>
        <div style={rs.kicker}>Take to</div>
        <div style={rs.big}>Bay {rt.physicalBay || "—"}</div>
        <div style={rs.driver}>{rt.login}</div>
        <div style={rs.meta}>
          {rt.wave ? `${rt.wave === "1" ? "1st" : rt.wave === "2" ? "2nd" : rt.wave} wave` : "wave —"}
          <span style={rs.dot}>·</span>{rt.area || "—"}
          <span style={rs.dot}>·</span>programmed {rt.programmedFull}
        </div>
        {moved && <div style={rs.note}>Parked in bay {rt.physicalBay} — programmed for bay {rt.programmedBay}</div>}
        <div style={rs.addr}>{result.num != null && <span style={rs.num}>{result.num}</span>} {result.name}</div>
        {ranges.length > 0 && result.num != null && <div style={rs.note}>{result.num} falls in {rangeText(ranges)} → {rt.login}</div>}
        {result.multi && <div style={rs.note}>Also on: {result.multi.slice(1).map((r) => `${r.rt.login} (bay ${r.rt.physicalBay})`).join(", ")}</div>}
      </>
    );
  } else if (result.status === "ask" || result.status === "outofrange") {
    bg = "linear-gradient(160deg, #c98a1e, #a06a12)";
    body = (
      <>
        <div style={rs.kicker}>{result.status === "outofrange" ? `No. ${result.num} not in any range` : "Which house number?"}</div>
        <div style={{ ...rs.big, fontSize: "clamp(2rem,9vw,4rem)" }}>{result.name}</div>
        <div style={{ ...rs.driver, fontSize: "1rem", fontWeight: 700 }}>Shared street — the number decides the driver &amp; bay</div>
        <div style={rs.options}>
          {result.options.map((o, i) => (
            <div key={i}><strong>Bay {o.rt.physicalBay}</strong> · {o.rt.login} — {rangeText(o.ranges)}</div>
          ))}
        </div>
        <div style={{ marginTop: 18, display: "flex", gap: 8 }}>
          <input value={askVal} onChange={(e) => setAskVal(e.target.value)} inputMode="numeric" placeholder="No." style={rs.numInput} />
          <button onClick={() => { const v = parseInt(askVal, 10); if (!isNaN(v) && onEnterNumber) onEnterNumber(v); }} style={rs.ghostBtn}>Find</button>
        </div>
      </>
    );
  } else {
    // 'no' (not on any route) or 'unreadable' (bad/dirty label) → flashing red
    bg = "linear-gradient(160deg, #cc5136, #9e3620)";
    flashing = true;
    body = (
      <>
        <div style={rs.kicker}>{result.status === "unreadable" ? "Couldn’t read the label" : "Not on any route"}</div>
        <div style={{ ...rs.big, fontSize: "clamp(2rem,8vw,3.4rem)" }}>
          {result.status === "unreadable" ? "Bad / dirty label" : (result.name || "Unknown address")}
        </div>
        <div style={rs.note}>
          {result.status === "unreadable"
            ? "Re-scan straighter and closer, or type the address in."
            : "No driver’s route covers this street. Check the address or ask the office."}
        </div>
        {(result.near || []).length > 0 && (
          <div style={{ ...rs.note, marginTop: 10 }}>
            Spelt almost the same on a round — check before you drop it:
            {result.near.map((n, i) => (
              <div key={i} style={{ fontWeight: 700 }}>“{n.entry}” · {n.rt.login} · Bay {n.rt.physicalBay || n.rt.programmedBay || "—"}</div>
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <div style={{ ...rs.overlay, background: bg }} className={flashing ? "hp-flash" : undefined}>
      <button onClick={onClose} style={rs.close}><X size={22} /></button>
      <div style={rs.icon}>
        {result.status === "ok" ? <CheckCircle2 size={40} /> : (result.status === "ask" || result.status === "outofrange") ? <MapPin size={40} /> : <AlertTriangle size={40} />}
      </div>
      <div style={rs.content}>
        {body}
        {showChooser && (
          <div style={rs.chooser}>
            <div style={rs.chooserQ}>The label has more than one number — which is the street number?</div>
            <div style={rs.chips}>
              {cands.map((n) => (
                <button
                  key={n}
                  onClick={() => onEnterNumber(n)}
                  style={{ ...rs.chip, ...(result.num === n ? rs.chipOn : null) }}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <button onClick={onScanAgain} style={rs.again}><RotateCcw size={16} /> Scan another</button>
    </div>
  );
}

const FLASH_CSS = `
@keyframes hp-spin { to { transform: rotate(360deg); } }
@keyframes hp-flash-kf { 0%,100% { filter: brightness(1); } 50% { filter: brightness(1.28); } }
.hp-flash { animation: hp-flash-kf 0.62s ease-in-out infinite; }
`;

const ink = "#2C2C2A", muted = "#5F5E5A", hair = "#D3D1C7";
const S = {
  trigger: {
    display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px", fontSize: 13, fontFamily: "inherit",
    fontWeight: 600, color: "#fff", background: "#1D9E75", border: "none", borderRadius: 8, cursor: "pointer",
  },
  modalWrap: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center" },
  modal: {
    width: "100%", maxWidth: 520, background: "#fff", borderTopLeftRadius: 18, borderTopRightRadius: 18,
    border: `1px solid ${hair}`, padding: 20, maxHeight: "92vh", overflowY: "auto",
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif", color: ink,
  },
  modalHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 },
  modalTitle: { fontSize: 18, fontWeight: 600 },
  modalSub: { fontSize: 13, color: muted, margin: "0 0 14px", lineHeight: 1.5 },
  iconBtn: { background: "transparent", border: "none", color: muted, cursor: "pointer" },
  reading: { display: "flex", alignItems: "center", gap: 8, marginTop: 12, color: muted, fontSize: 13 },
  modalErr: { marginTop: 12, fontSize: 13, color: "#A32D2D", fontWeight: 600 },
  orBlock: { borderTop: `1px solid ${hair}`, margin: "16px 0 4px", paddingTop: 14 },
  orLabel: { fontSize: 11, fontWeight: 700, color: muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 },
  input: { flex: 1, background: "#fff", border: `1px solid ${hair}`, borderRadius: 9, color: ink, fontSize: 15, padding: "9px 11px", fontFamily: "inherit", boxSizing: "border-box" },
  findBtn: { display: "inline-flex", alignItems: "center", gap: 6, background: "#1D9E75", color: "#fff", border: "none", borderRadius: 9, padding: "0 16px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
};

const rs = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 300, color: "#fff",
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    padding: 24, textAlign: "center",
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  close: { position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.16)", border: "none", color: "#fff", borderRadius: 999, padding: 8, cursor: "pointer", display: "flex" },
  icon: { opacity: 0.92, marginBottom: 6 },
  content: { maxWidth: 560 },
  again: { marginTop: 28, display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.16)", border: "2px solid rgba(255,255,255,0.6)", color: "#fff", borderRadius: 12, padding: "10px 20px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
};
const rsShared = {
  kicker: { fontSize: 14, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.85 },
  big: { fontSize: "clamp(3rem, 15vw, 6.5rem)", fontWeight: 800, lineHeight: 1.02, margin: "2px 0 4px" },
  driver: { fontSize: "clamp(1.3rem, 6vw, 2.2rem)", fontWeight: 800, letterSpacing: "0.02em" },
  meta: { fontSize: 15, fontWeight: 600, opacity: 0.92, marginTop: 8 },
  dot: { opacity: 0.6, margin: "0 7px" },
  note: { fontSize: 14, opacity: 0.9, marginTop: 12, lineHeight: 1.5, maxWidth: 460, marginLeft: "auto", marginRight: "auto" },
  addr: { fontSize: 16, fontWeight: 700, marginTop: 16, opacity: 0.95 },
  num: { fontSize: 20, fontWeight: 800 },
  options: { marginTop: 16, display: "flex", flexDirection: "column", gap: 6, fontSize: 15 },
  numInput: { width: 130, textAlign: "center", fontSize: 24, fontWeight: 800, border: "none", borderRadius: 12, padding: 8, color: ink },
  ghostBtn: { fontWeight: 800, fontSize: 16, border: "2px solid #fff", background: "transparent", color: "#fff", borderRadius: 12, padding: "0 18px", cursor: "pointer", fontFamily: "inherit" },
  chooser: { marginTop: 22, borderTop: "1px solid rgba(255,255,255,0.25)", paddingTop: 14 },
  chooserQ: { fontSize: 13, fontWeight: 700, opacity: 0.92, marginBottom: 10, lineHeight: 1.4 },
  chips: { display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" },
  chip: { minWidth: 54, fontSize: 20, fontWeight: 800, padding: "8px 14px", borderRadius: 12, cursor: "pointer", color: "#fff", background: "rgba(255,255,255,0.16)", border: "2px solid rgba(255,255,255,0.5)", fontFamily: "inherit" },
  chipOn: { background: "#fff", color: ink, border: "2px solid #fff" },
};
Object.assign(rs, rsShared);
